package com.arena_web.ui.sections.chat.mobile

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import com.arena_web.data.dto.ChatListItemDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.EMPTY_STATE_GLYPH_COLOR
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input

enum class MobileChatFilter { All, Direct, Community }

// Figma 10093:322650 — mobile Chat page content.
@Composable
fun MobileChatList(
    chats: List<ChatListItemDto>,
    searchQuery: String,
    onSearchQueryChanged: (String) -> Unit,
    onChatSelected: (String) -> Unit,
    onNewChatClick: () -> Unit,
) {
    val t = LocalAppStrings.current
    var filter by remember { mutableStateOf(MobileChatFilter.All) }

    val visibleChats = remember(chats, filter) {
        when (filter) {
            MobileChatFilter.All -> chats
            MobileChatFilter.Direct -> chats.filter { it.type.uppercase() == "DIRECT" }
            MobileChatFilter.Community -> chats.filter { it.type.uppercase() == "GROUP" }
        }
    }
    val directUnread = remember(chats) {
        chats.filter { it.type.uppercase() == "DIRECT" }.sumOf { it.unreadCount }
    }
    val communityUnread = remember(chats) {
        chats.filter { it.type.uppercase() == "GROUP" }.sumOf { it.unreadCount }
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            // No BottomNavBar on the chat surface anymore — only the home
            // indicator needs clearing, not the 110-px nav band.
            .padding(top = 12.px)
            .styleModifier {
                property("padding-bottom", "max(16px, env(safe-area-inset-bottom, 0px))")
            }
            .gap(18.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Search row: rounded search input + "+" button.
        Row(
            modifier = Modifier.fillMaxWidth().gap(8.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Row(
                modifier = Modifier
                    .flexGrow(1)
                    .height(44.px)
                    .borderRadius(28.px)
                    .padding(leftRight = 12.px)
                    .gap(6.px)
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "${AppGradients.InteractiveSecondary} padding-box, " +
                                "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                    },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                // CSS-mask + background-color recolours the monochrome SVG
                // to an exact hex (#818D9C) without needing a filter chain.
                Box(
                    modifier = Modifier
                        .size(16.px)
                        .flexShrink(0)
                        .styleModifier {
                            property("background-color", "#818D9C")
                            property("mask", "url('${AppIcons.Search}') no-repeat center / contain")
                            property("-webkit-mask", "url('${AppIcons.Search}') no-repeat center / contain")
                        }
                )
                Input(
                    type = InputType.Text,
                    attrs = {
                        value(searchQuery)
                        placeholder(t.chatSearchPlaceholder)
                        onInput { onSearchQueryChanged(it.value) }
                        style {
                            property("flex", "1")
                            property("background", "transparent")
                            property("border", "none")
                            property("outline", "none")
                            property("color", "var(--arena-text-2)")
                            property("font-family", "'Urbanist', sans-serif")
                            property("font-size", "16px")
                            property("min-width", "0")
                        }
                    }
                )
            }
            Box(
                modifier = Modifier
                    .size(44.px)
                    .borderRadius(22.px)
                    .cursor(Cursor.Pointer)
                    .onClick { onNewChatClick() }
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "${AppGradients.InteractiveSecondary} padding-box, " +
                                "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                    },
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    src = AppIcons.Add,
                    modifier = Modifier.size(18.px).styleModifier {
                        property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V)
                    }
                )
            }
        }

        // Segmented control — All / Direct / Community with unread badges,
        // distributed evenly across the row.
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(48.px)
                .borderRadius(34.px)
                .padding(4.px)
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                },
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            FilterChip(
                label = t.chatFilterAll,
                active = filter == MobileChatFilter.All,
                badge = null,
                onClick = { filter = MobileChatFilter.All },
            )
            FilterChip(
                label = t.chatFilterDirect,
                active = filter == MobileChatFilter.Direct,
                badge = directUnread.takeIf { it > 0 }?.toString(),
                onClick = { filter = MobileChatFilter.Direct },
            )
            FilterChip(
                label = t.chatFilterCommunity,
                active = filter == MobileChatFilter.Community,
                badge = communityUnread.takeIf { it > 0 }?.toString(),
                onClick = { filter = MobileChatFilter.Community },
            )
        }

        when {
            // Search yielded nothing — keep it lightweight, not the full state.
            visibleChats.isEmpty() && searchQuery.isNotBlank() -> Box(
                modifier = Modifier.fillMaxWidth().flexGrow(1).styleModifier { property("min-height", "0") },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    t.chatNoChatsFound,
                    modifier = Modifier.fontSize(14.px).color(Color("var(--arena-text-muted)")),
                )
            }

            // No conversations yet — rich empty state (Figma 10272:226903).
            visibleChats.isEmpty() -> Box(
                modifier = Modifier.fillMaxWidth().flexGrow(1).styleModifier { property("min-height", "0") },
                contentAlignment = Alignment.Center,
            ) {
                ChatsEmptyState()
            }

            // Chat list — scrollable column.
            else -> Div(
                attrs = Modifier
                    .fillMaxWidth()
                    .flexGrow(1)
                    .styleModifier {
                        property("min-height", "0")
                        property("overflow-y", "auto")
                        property("scrollbar-width", "none")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "0")
                    }
                    .toAttrs()
            ) {
                visibleChats.forEach { chat ->
                    ChatRow(
                        chat = chat,
                        onClick = { onChatSelected(chat.id) },
                    )
                }
            }
        }
    }
}

// Empty state for the chat list (Figma 10272:226903) — chatbubbles icon +
// title + subtitle, centered in the list area. Raw px to match the page (this
// is a full mobile page, not a `--scale`-aliased bottom sheet).
@Composable
private fun ChatsEmptyState() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().padding(leftRight = 24.px).gap(22.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Muted-blue tint via mask — the app-wide canonical empty-state glyph
        // colour (matches desktop TintedGlyph), not the SVG's raw fill.
        Box(
            modifier = Modifier.size(60.px).styleModifier {
                property("background-color", EMPTY_STATE_GLYPH_COLOR)
                property("-webkit-mask", "url('${AppIcons.EmptyChats}') no-repeat center / contain")
                property("mask", "url('${AppIcons.EmptyChats}') no-repeat center / contain")
            }
        )
        Column(
            modifier = Modifier.fillMaxWidth().gap(8.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.chatEmptyTitle,
                modifier = Modifier
                    .fontSize(24.px)
                    .fontWeight(AppTypography.Medium)
                    .lineHeight(32.px)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .textAlign(TextAlign.Center),
            )
            SpanText(
                t.chatEmptySubtitle,
                modifier = Modifier
                    .fillMaxWidth()
                    .fontSize(14.px)
                    .fontWeight(AppTypography.Regular)
                    .lineHeight(22.px)
                    .color(Color("var(--arena-text-muted)"))
                    .textAlign(TextAlign.Center),
            )
        }
    }
}

@Composable
private fun FilterChip(
    label: String,
    active: Boolean,
    badge: String?,
    onClick: () -> Unit,
) {
    Row(
        modifier = Modifier
            .height(40.px)
            .borderRadius(28.px)
            .padding(leftRight = if (active) 26.px else 18.px)
            .gap(8.px)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .then(
                if (active) Modifier.backgroundColor(Color("#0061FF"))
                else Modifier
            ),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Center,
    ) {
        SpanText(
            label,
            modifier = Modifier
                .fontSize(14.px)
                .fontWeight(600)
                .lineHeight(14.px)
                .color(if (active) org.jetbrains.compose.web.css.Color(Color.white.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
                .styleModifier { property("white-space", "nowrap") }
        )
        if (badge != null) {
            Box(
                modifier = Modifier
                    .size(20.px)
                    .borderRadius(50.percent)
                    .backgroundColor(Color("#0061FF")),
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    badge,
                    modifier = Modifier
                        .fontSize(11.px)
                        .fontWeight(700)
                        .color(if (active) org.jetbrains.compose.web.css.Color(Color.white.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
        }
    }
}

@Composable
private fun ChatRow(
    chat: ChatListItemDto,
    onClick: () -> Unit,
) {
    // Transient press feedback — Figma 8294:278163 (navy fill + gradient
    // edge). Matches the desktop ChatListItem pattern: a 3 px transparent
    // border is always present so the row never shifts size — the press just
    // swaps it for a colored border + filled bg.
    val t = LocalAppStrings.current
    var pressed by remember { mutableStateOf(false) }
    // Player-card avatars need more breathing room because their visible
    // pixels don't fill the full bounding box; round group avatars already
    // hug their circle edge so 8 px is enough.
    val hasPlayerCard = chat.cardImageUrl != null
    val avatarGap = if (hasPlayerCard) "18px" else "8px"
    val scope = rememberCoroutineScope()
    var releaseJob by remember { mutableStateOf<kotlinx.coroutines.Job?>(null) }

    fun press() {
        releaseJob?.cancel()
        releaseJob = null
        pressed = true
    }
    // Holds the highlight for ~220 ms after release so the user sees the
    // tap register before it fades. The bg `transition` smooths the visual.
    fun release() {
        if (!pressed) return
        releaseJob?.cancel()
        releaseJob = scope.launch {
            kotlinx.coroutines.delay(220)
            pressed = false
        }
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .onMouseDown { press() }
            .onMouseUp { release() }
            .onMouseLeave { release() }
            .onTouchStart { press() }
            .onTouchEnd { release() }
            .onTouchCancel { release() }
            .styleModifier {
                property("gap", avatarGap)
                property("transition", "background 0.25s ease")
                property("border", "3px solid transparent")
                property("border-radius", "14px")
                // Round group avatars sit tight against the row edge — drop
                // the left padding for them so the 40 px circle visually
                // aligns with the search input above. Player-card rows keep
                // 12 px to compensate for the card's transparent borders.
                val leftPad = if (hasPlayerCard) "12px" else "4px"
                property("padding", "12px 16px 12px $leftPad")
                if (pressed) {
                    property(
                        "background",
                        "linear-gradient(var(--l-hover2, #091a40), var(--l-hover2, #091a40)) padding-box, " +
                            "linear-gradient(90deg, #0061FF, #3C79F100 5%) border-box"
                    )
                }
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // Avatar — mini player card for direct chats with card data, else
        // a round avatar with image or initials.
        val cardFrameUrl = chat.cardImageUrl
        if (cardFrameUrl != null) {
            // CSS scale enlarges the XtraSmall player card visually without
            // bumping it to a larger design size. transform-origin keeps the
            // top-left anchor so the row's vertical rhythm stays steady.
            Box(
                modifier = Modifier
                    .flexShrink(0)
                    .styleModifier {
                        property("width", "calc(58px * var(--mobile-scale, 0.4) * 1.5)")
                        property("height", "calc(90px * var(--mobile-scale, 0.4) * 1.5)")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                    }
            ) {
                Box(
                    modifier = Modifier.styleModifier {
                        property("transform", "scale(1.5)")
                        property("transform-origin", "center")
                    }
                ) {
                    PlayerCard(
                        playerName = "",
                        rating = 0,
                        position = "",
                        sport = "football",
                        size = PlayerCardSize.XtraSmall,
                        cardAssetsBasePath = null,
                        cardTextColor = null,
                        cardFrameUrl = cardFrameUrl,
                        avatarUrl = chat.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                        hideRating = true,
                        hideShadow = true,
                    )
                }
            }
        } else {
            RoundAvatar(name = chat.name.orEmpty(), imageUrl = chat.imageUrl)
        }

        // Name + last message.
        Column(
            modifier = Modifier
                .flexGrow(1)
                .styleModifier { property("min-width", "0") }
                .gap(2.px),
        ) {
            SpanText(
                chat.name.orEmpty().ifBlank { t.chatUnnamed },
                modifier = Modifier
                    .fontSize(16.px)
                    .fontWeight(500)
                    .lineHeight(18.px)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier {
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                    }
            )
            SpanText(
                chat.lastMessage?.content?.takeIf { it.isNotBlank() } ?: t.chatNoMessagesYet,
                modifier = Modifier
                    .fontSize(14.px)
                    .lineHeight(22.px)
                    .color(Color("var(--arena-text-muted)"))
                    .styleModifier {
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                    }
            )
        }

        // Time + unread badge.
        Column(
            modifier = Modifier.gap(8.px),
            horizontalAlignment = Alignment.End,
        ) {
            SpanText(
                formatChatTime(chat.lastMessage?.createdAt ?: chat.createdAt),
                modifier = Modifier
                    .fontSize(12.px)
                    .lineHeight(12.px)
                    .color(Color("var(--arena-text-muted)"))
                    .styleModifier { property("white-space", "nowrap") }
            )
            if (chat.unreadCount > 0) {
                Box(
                    modifier = Modifier
                        .size(20.px)
                        .borderRadius(50.percent)
                        .backgroundColor(Color("#0061FF")),
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        chat.unreadCount.toString(),
                        modifier = Modifier
                            .fontSize(11.px)
                            .fontWeight(700)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
            }
        }
    }
}

@Composable
private fun RoundAvatar(name: String, imageUrl: String?) {
    val initials = name.split(" ")
        .filter { it.isNotBlank() }
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "?" }
    Box(
        modifier = Modifier
            .size(40.px)
            .flexShrink(0)
            .borderRadius(50.percent)
            .overflow(Overflow.Hidden)
            .backgroundColor(Color("#1A2238")),
        contentAlignment = Alignment.Center,
    ) {
        if (imageUrl != null) {
            Div(attrs = {
                ref { el ->
                    el.innerHTML = "<img src=\"$imageUrl\" referrerpolicy=\"no-referrer\" " +
                        "style=\"width:100%;height:100%;object-fit:cover;\" />"
                    onDispose {}
                }
                style { property("width", "100%"); property("height", "100%") }
            })
        } else {
            SpanText(
                initials,
                modifier = Modifier
                    .fontSize(16.px)
                    .color(Color("#FAFAFB"))
            )
        }
    }
}

// "2:34 PM" if today, "Yesterday", weekday name within the last week, else
// "DD MMM". Matches Figma's display variants.
private fun formatChatTime(iso: String?): String {
    if (iso.isNullOrBlank()) return ""
    return try {
        val date = kotlin.js.Date(iso)
        val now = kotlin.js.Date()
        val sameDay = date.getDate() == now.getDate() &&
            date.getMonth() == now.getMonth() &&
            date.getFullYear() == now.getFullYear()
        if (sameDay) {
            val h24 = date.getHours()
            val mins = date.getMinutes()
            val period = if (h24 < 12) "AM" else "PM"
            val hh = ((h24 % 12).let { if (it == 0) 12 else it })
            val mm = mins.toString().padStart(2, '0')
            "$hh:$mm $period"
        } else {
            val msPerDay = 86_400_000.0
            val diffDays = ((now.getTime() - date.getTime()) / msPerDay).toInt()
            when {
                diffDays == 1 -> "Yesterday"
                diffDays in 2..6 -> arrayOf(
                    "Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"
                )[date.getDay()]
                else -> {
                    val months = arrayOf(
                        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
                    )
                    "${date.getDate()} ${months[date.getMonth()]}"
                }
            }
        }
    } catch (_: Throwable) {
        ""
    }
}
